import React, { useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaItemDetails.module.css";
import { AGENDAITEM_PROPERTY, ProtocolTagKeys } from "src/stores/assemblies/AgendaItemStore";
import { CurrentAgendaItemDocuments } from "./documents/CurrentAgendaItemDocuments";
import { AddAgendaDocumentsModal } from "src/components/assemblies/agenda/list/add-documents-modal/AddAgendaDocumentsModal";
import { Editor, EditorEvents } from "@tiptap/react";
import { AgendaVotingProgressModal } from "./progress-modal/AgendaVotingProgressModal";
import { getHtmlStringForPreview, getHtmlStringForTipTap } from "src/utils/HTMLUtils";
import { CurrentAgendaItemVotings } from "./voting/CurrentAgendaItemVotings";
import RichTextEditor from "src/components/tiptap/RichTextEditor";

const AgendaItemDetailsBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, navStore, votingStore } = useContext(RootStoreContext);
    const protocolTextRichEditorTextRef = useRef<Editor | null>(null);
    const titleInputWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (agendaitemStore.currentAgendaItem?.id) {
            titleInputWrapperRef.current?.querySelector("input")?.focus();
        }
    }, [agendaitemStore.currentAgendaItem?.id]);

    const onAgendaItemProtocolTextRichTextEditorCreated = useCallback((editor: EditorEvents["create"]) => {
        protocolTextRichEditorTextRef.current = editor.editor;
    }, []);

    const onChangeAgendaItemField = useCallback(
        (agendaItemProperty: AGENDAITEM_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (agendaitemStore.currentAgendaItem) {
                agendaitemStore.updateAgendaItem(agendaItemProperty, newValue);
            }

            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    const documentTile = () => {
        if (
            agendaitemStore.currentAgendaItem?.agendaitemfiles &&
            agendaitemStore.currentAgendaItem?.agendaitemfiles.length > 0
        ) {
            return <CurrentAgendaItemDocuments />;
        }
    };

    const onChangeAgendaItemRichTextEditorField = useCallback(
        (agendaItemProperty: AGENDAITEM_PROPERTY) => (update: EditorEvents["update"]) => {
            if (agendaitemStore.currentAgendaItem) {
                if (update.editor.getText().length === 0) {
                    // If the editor content is getting emptied we want to remove the empty paragraph tag
                    agendaitemStore.updateAgendaItem(agendaItemProperty, "");
                } else {
                    agendaitemStore.updateAgendaItem(
                        agendaItemProperty,
                        getHtmlStringForPreview(update.editor.getHTML())
                    );
                }
            }
            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    const externalDocumentInputRef = useRef<HTMLInputElement>(null);

    const onExternalDocumentFileSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file || !agendaitemStore.currentAgendaItem?.id) return;
            agendaitemStore.setSelectedExternalAgendaFile(file);
            await agendaitemStore.uploadExternalAgendaFile(agendaitemStore.currentAgendaItem.id);
        },
        [agendaitemStore]
    );

    const onClickAddExternalDocument = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        externalDocumentInputRef.current?.click();
    }, []);

    const onClickAddErpDocument = useCallback(
        async (e: any) => {
            e.preventDefault();

            assemblyStore.setIsAddingDocumentsDisplayed(true);
        },
        [assemblyStore]
    );

    const onClickOpenVotingModal = useCallback(
        async (e: any) => {
            e.preventDefault();
            await votingStore.loadVotingInformationBasis();
            votingStore.setIsNewVoting(true);
            await votingStore.setIsVotingModalDisplayed(true);
            await votingStore.resetVotingBasis();
        },
        [votingStore]
    );

    const onClickProtocolTag = useCallback(
        (translationKey: string) => (e: any) => {
            if (agendaitemStore.currentAgendaItem) {
                // Insert the tag at the end of the document
                protocolTextRichEditorTextRef.current
                    ?.chain()
                    .command(({ tr: transcation }) => {
                        transcation.insertText(
                            t(translationKey),
                            protocolTextRichEditorTextRef.current?.state.doc.content.size || 0
                        );
                        return true;
                    })
                    .run();

                // Set the cursor to the end of the document
                protocolTextRichEditorTextRef.current
                    ?.chain()
                    .focus()
                    .setTextSelection(protocolTextRichEditorTextRef.current?.state.doc.content.size)
                    .run();
            }
        },
        [agendaitemStore.currentAgendaItem, t]
    );

    const protocolTag = () => {
        return Object.values(ProtocolTagKeys).map((translationKey) => (
            <div key={translationKey} className={styles.Tags} onClick={onClickProtocolTag(translationKey)}>
                {t(translationKey)} <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.AddIconTags} />
            </div>
        ));
    };

    return (
        <div className={styles.ExpandedContent}>
            <div ref={titleInputWrapperRef}>
                <UI.Input
                    className={styles.AgendaInput}
                    value={agendaitemStore.currentAgendaItem?.text || ""}
                    onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.TEXT)}
                    label={`${t("screens.assemblies.agenda.title")} *`}
                    disabled={assemblyStore.isAssemblyReadOnly}
                />
            </div>
            <div className={styles.AgendaItemDescription}>
                <UI.Form.Label>{t("screens.assemblies.agenda.description")}</UI.Form.Label>
                <RichTextEditor
                    key={agendaitemStore.currentAgendaItem?.id + "-description"}
                    enableContentCheck={true}
                    content={getHtmlStringForTipTap(agendaitemStore.currentAgendaItem?.description)}
                    onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.DESCRIPTION)}
                    disabled={assemblyStore.isAssemblyReadOnly}
                />
            </div>
            <div className={styles.AgendaItemComment}>
                <UI.Form.Label>{t("screens.assemblies.agenda.internalcomments")}</UI.Form.Label>
                <RichTextEditor
                    key={agendaitemStore.currentAgendaItem?.id + "-internal"}
                    enableContentCheck={true}
                    content={getHtmlStringForTipTap(agendaitemStore.currentAgendaItem?.internalcomments)}
                    placeholder={t("screens.assemblies.agenda.internalcomments_placeholder").toString()}
                    onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.INTERNALCOMMENTS)}
                    disabled={assemblyStore.isAssemblyReadOnly}
                />
            </div>
            {navStore.hasFeatureFlagAssembly ? (
                <div className={styles.ProtocolContainer}>
                    <UI.Form.Label>{t("screens.assemblies.agenda.protocoltext")}</UI.Form.Label>
                    {assemblyStore.isAssemblyReadOnly ? undefined : (
                        <div className={assemblyStore.isAssemblyReadOnly ? undefined : styles.ProtocolTags}>
                            {protocolTag()}
                        </div>
                    )}
                    <RichTextEditor
                        key={agendaitemStore.currentAgendaItem?.id + "-protocol"}
                        enableContentCheck={true}
                        content={getHtmlStringForTipTap(agendaitemStore.currentAgendaItem?.protocoltext)}
                        placeholder={
                            assemblyStore.isAssemblyReadOnly
                                ? ""
                                : t("screens.assemblies.agenda.protocol_placeholder").toString()
                        }
                        onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.PROTOCOLTEXT)}
                        onEditorCreate={onAgendaItemProtocolTextRichTextEditorCreated}
                        disabled={assemblyStore.isAssemblyReadOnly}
                    />
                </div>
            ) : undefined}

            {assemblyStore.isAssemblyReadOnly ? undefined : (
                <>
                    <div className={styles.AddExternalDocument} onClick={onClickAddExternalDocument}>
                        <UI.Icon icon={UI.SVGIcon.Documents} size={26} className="mr-2" />
                        <div>{t("screens.assemblies.agenda.add_document")}</div>
                    </div>
                    <input
                        ref={externalDocumentInputRef}
                        type="file"
                        hidden
                        onChange={onExternalDocumentFileSelected}
                    />
                    <> {assemblyStore.isAgendaItemTemplate === false ? <AddAgendaDocumentsModal /> : undefined}</>
                    <div className={styles.AddERPDocument} onClick={onClickAddErpDocument}>
                        <UI.Icon icon={UI.SVGIcon.Add} size={14} className={styles.IconAddERPDocument} />
                        <div>{t("screens.assemblies.agenda.select_document")}</div>
                    </div>
                </>
            )}
            {documentTile()}
            {navStore.hasFeatureFlagAssemblyVoting && !assemblyStore.isAssemblyReadOnly ? (
                <>
                    <div className={styles.Voting} onClick={onClickOpenVotingModal}>
                        <UI.Icon icon={UI.SVGIcon.Voting} size={28} className={styles.VotingIcon} />
                        <div>{t("screens.assemblies.vote.votings")}</div>
                    </div>

                    <CurrentAgendaItemVotings />
                </>
            ) : assemblyStore.isAssemblyReadOnly ? (
                <CurrentAgendaItemVotings />
            ) : undefined}

            <AgendaVotingProgressModal />
        </div>
    );
});

export const AgendaItemDetails = Sentry.withProfiler(AgendaItemDetailsBase);
